import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { useDesignStore } from 'context/DesignContext';
import {
  HighHeel_heelCenterIndex,
  HighHeelCurve,
  HighHeelDragIndex,
  HighHeelHeight,
  HighHeelInsoleIndices,
} from 'data/highHeelJoints';
import { BONES_X_SHIFT } from 'models/Bones';
import { INSOLE_LENGTH_MM } from 'models/Insole';
import { DEFAULT_MESH_COLOR } from 'components/Viewer3d/consts';
import { HighLevelJoint, meshConfigs } from 'types/stl';

const HEEL_MESH_URL = `${process.env.PUBLIC_URL}/assets/heel_insole_v01.stl`;
const DRAG_SPHERE_RADIUS = 2.5;
const DRAG_SPHERE_COLOR = 0x00aaff;

export type HighHeelBuildResult = {
  meshes: THREE.Mesh[];
  curveGroups: THREE.Group[];
  dragSpheres: THREE.Mesh[];
  initialHeight: number;
};

export type HighHeelFeetView = 'both' | 'right' | 'left';

export const HIGH_HEEL_JOINT_TYPE = 'highHeel';

/** Drops every face that touches the insole region so it is not rendered. */
export const hideHighHeelInsoleFaces = (geometry: THREE.BufferGeometry): void => {
  const hideSet = new Set(HighHeelInsoleIndices);
  if (!geometry.index) {
    const position = geometry.getAttribute('position');
    geometry.setIndex(Array.from({ length: position.count }, (_, i) => i));
  }

  const index = geometry.index!;
  const filtered: number[] = [];
  for (let i = 0; i < index.count; i += 3) {
    const a = index.getX(i);
    const b = index.getX(i + 1);
    const c = index.getX(i + 2);
    if (hideSet.has(a) || hideSet.has(b) || hideSet.has(c)) continue;
    filtered.push(a, b, c);
  }

  geometry.setIndex(filtered);
};

/**
 * Owns the high-heel meshes, undeformed geometries, height joints, curve groups
 * and the Y-drag handles. Handles are dragged through the viewer's joint
 * DragControls (not a separate DragControls instance).
 */
export class HighHeelController {
  private meshes: THREE.Mesh[] = [];
  private baseGeometries: THREE.BufferGeometry[] = [];
  private heightJoints: HighLevelJoint[] = [];
  private curveGroups: THREE.Group[] = [];
  private dragSpheres: THREE.Mesh[] = [];
  private dragVertexId = HighHeelDragIndex;

  getMeshes(): THREE.Mesh[] {
    return this.meshes;
  }

  getCurveGroups(): THREE.Group[] {
    return this.curveGroups;
  }

  getDragSpheres(): THREE.Mesh[] {
    return this.dragSpheres;
  }

  setMeshesVisible(visible: boolean, feetView: HighHeelFeetView = 'both'): void {
    this.meshes.forEach((mesh, foot) => {
      if (mesh) mesh.visible = visible && isFootVisible(foot, feetView);
    });
  }

  /**
   * Loads the heel STL and builds a mesh + drag sphere per foot, aligned to that
   * foot's insole. Returns null when the mesh cannot be used.
   */
  async build(): Promise<HighHeelBuildResult | null> {
    let geometry: THREE.BufferGeometry | null = null;
    let material: THREE.MeshStandardMaterial | null = null;

    try {
      const loadedGeometry = await new STLLoader().loadAsync(HEEL_MESH_URL);
      loadedGeometry.deleteAttribute('normal');
      geometry = BufferGeometryUtils.mergeVertices(loadedGeometry);
      loadedGeometry.dispose();
      geometry.computeVertexNormals();
      hideHighHeelInsoleFaces(geometry);

      const positions = geometry.getAttribute('position');
      const centerId = HighHeelHeight.centerId;
      if (centerId >= positions.count) return null;

      this.dragVertexId = resolveDragVertexId(positions.count);

      material = new THREE.MeshStandardMaterial({
        color: DEFAULT_MESH_COLOR,
        roughness: 0.7,
        metalness: 0,
        side: THREE.DoubleSide,
      });

      const center = new THREE.Vector3().fromBufferAttribute(positions, centerId);
      const initialHeight = Math.round(center.y * 10) / 10;

      this.reset();

      [0, 1].forEach(foot => {
        const meshGeometry = geometry!.clone();
        const mesh = new THREE.Mesh(meshGeometry, material!.clone());
        mesh.name = `heel_HS005_${foot === 0 ? 'right' : 'left'}`;
        mesh.scale.x = foot === 0 ? 1 : -1;
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        this.meshes[foot] = mesh;
        this.baseGeometries[foot] = meshGeometry.clone();
        this.heightJoints[foot] = {
          ...HighHeelHeight,
          center: center.clone(),
          value: initialHeight,
          vertices: HighHeelHeight.vertices.map(group => ({
            ids: [...group.ids],
            weight: group.weight,
          })),
        };

        const insoleGeo = useDesignStore.getState().insoleDesign?.insoles[foot]?.lowpolyBufferGeometry;
        if (!insoleGeo) return;
        insoleGeo.computeBoundingBox();
        const box = insoleGeo.boundingBox;
        const length = box!.max.z - box!.min.z;
        this.scaleWithLength(foot, length / INSOLE_LENGTH_MM, insoleGeo);
        this.dragSpheres[foot] = createDragSphere(mesh, this.dragVertexId, foot);
      });

      const curveGroups = this.rebuildCurveGroups();

      return {
        meshes: this.meshes,
        curveGroups,
        dragSpheres: this.dragSpheres,
        initialHeight,
      };
    } catch (error) {
      console.error('Failed to load the high-heel mesh:', error);
      return null;
    } finally {
      geometry?.dispose();
      material?.dispose();
    }
  }

  /** Moves the heel vertices to the requested height and refreshes the curve + handle. */
  setHeight(height: number, foot: number): THREE.Group[] | null {
    const mesh = this.meshes[foot];
    const baseGeometry = this.baseGeometries[foot];
    const joint = this.heightJoints[foot];
    if (!mesh || !baseGeometry || !joint || !Number.isFinite(height)) return null;

    const clamped = THREE.MathUtils.clamp(height, HighHeelHeight.min, HighHeelHeight.max);

    mesh.geometry.dispose();
    mesh.geometry = baseGeometry.clone();

    const positions = mesh.geometry.getAttribute('position');
    const basePositions = baseGeometry.getAttribute('position');
    const targetY = (clamped - joint.cval) / joint.bval;
    const displacement = targetY - basePositions.getY(joint.centerId);

    joint.vertices.forEach(vertexGroup => {
      vertexGroup.ids.forEach(vertexId => {
        if (vertexId < 0 || vertexId >= positions.count) return;
        positions.setY(
          vertexId,
          basePositions.getY(vertexId) + displacement * vertexGroup.weight
        );
      });
    });

    // Keep the drag vertex moving even when it is not part of a weight group.
    if (
      this.dragVertexId >= 0 &&
      this.dragVertexId < positions.count &&
      !joint.vertices.some(group => group.ids.includes(this.dragVertexId))
    ) {
      positions.setY(
        this.dragVertexId,
        basePositions.getY(this.dragVertexId) + displacement
      );
    }

    joint.value = clamped;
    joint.center.y = targetY;
    positions.needsUpdate = true;
    mesh.geometry.computeVertexNormals();
    mesh.geometry.computeBoundingBox();
    mesh.geometry.computeBoundingSphere();

    this.syncDragSphere(foot);
    return this.rebuildCurveGroups();
  }

  setDragHandlesVisible(visible: boolean, feetView: HighHeelFeetView = 'both'): void {
    this.dragSpheres.forEach((sphere, foot) => {
      if (sphere) sphere.visible = visible && isFootVisible(foot, feetView);
    });
  }

  /** Applies a joint-DragControls drag to a high-heel handle. Returns the new height. */
  applyHandleDrag(sphere: THREE.Mesh): number | null {
    const foot = sphere.userData.foot as number;
    const original = sphere.userData.originalPosition as THREE.Vector3;
    if (original) {
      sphere.position.x = original.x;
      sphere.position.z = original.z;
    }

    const height = this.heightFromSphere(sphere, foot);
    if (height == null) return null;

    this.setHeight(height, foot);
    return height;
  }

  /** Finishes a high-heel handle drag and returns the clamped height for that foot. */
  finishHandleDrag(sphere: THREE.Mesh): { foot: number; height: number } | null {
    const foot = sphere.userData.foot as number;
    const joint = this.heightJoints[foot];
    if (!joint) return null;

    this.syncDragSphere(foot);
    return { foot, height: joint.value };
  }

  /** Releases geometries and forgets every object built so far. */
  reset(): void {
    this.disposeDragSpheres();
    this.disposeCurveGroups();
    this.baseGeometries.forEach(geometry => geometry.dispose());
    this.meshes = [];
    this.baseGeometries = [];
    this.heightJoints = [];
    this.curveGroups = [];
    this.dragSpheres = [];
  }

  /** Converts the dragged sphere's world Y into a clamped high-heel height. */
  private heightFromSphere(sphere: THREE.Mesh, foot: number): number | null {
    const mesh = this.meshes[foot];
    const baseGeometry = this.baseGeometries[foot];
    const joint = this.heightJoints[foot];
    if (!mesh || !baseGeometry || !joint) return null;

    const basePositions = baseGeometry.getAttribute('position');
    if (this.dragVertexId < 0 || this.dragVertexId >= basePositions.count) return null;

    const local = mesh.worldToLocal(sphere.position.clone());
    const weight = findVertexWeight(joint, this.dragVertexId);
    const baseDragY = basePositions.getY(this.dragVertexId);
    const baseCenterY = basePositions.getY(joint.centerId);
    const targetY = weight === 0
      ? local.y
      : baseCenterY + (local.y - baseDragY) / weight;
    const height = targetY * joint.bval + joint.cval;
    return THREE.MathUtils.clamp(height, HighHeelHeight.min, HighHeelHeight.max);
  }

  private syncDragSphere(foot: number): void {
    const mesh = this.meshes[foot];
    const sphere = this.dragSpheres[foot];
    if (!mesh || !sphere) return;

    const positions = mesh.geometry.getAttribute('position');
    if (this.dragVertexId < 0 || this.dragVertexId >= positions.count) return;

    mesh.updateMatrixWorld(true);
    const world = mesh.localToWorld(
      new THREE.Vector3().fromBufferAttribute(positions, this.dragVertexId)
    );
    sphere.position.copy(world);
    sphere.userData.originalPosition = world.clone();
  }

  private alignToInsole(
    mesh: THREE.Mesh,
    foot: number,
    insoleGeometry?: THREE.BufferGeometry | null
  ): void {
    const geometry =
      insoleGeometry ??
      useDesignStore.getState().insoleDesign?.insoles[foot]?.lowpolyBufferGeometry;
    if (!geometry) return;

    mesh.updateMatrixWorld(true);

    const insolePositions = geometry.getAttribute('position');
    const heelPositions = mesh.geometry.getAttribute('position');
    if (
      meshConfigs.Insole.heelCenterIndex >= insolePositions.count ||
      HighHeel_heelCenterIndex >= heelPositions.count
    ) return;

    const insoleHeelCenter = new THREE.Vector3()
      .fromBufferAttribute(insolePositions, meshConfigs.Insole.heelCenterIndex);
    const highHeelCenter = new THREE.Vector3()
      .fromBufferAttribute(heelPositions, HighHeel_heelCenterIndex)
      .applyMatrix4(mesh.matrixWorld);

    const dist = insoleHeelCenter.sub(highHeelCenter);
    dist.y = 0;
    dist.x += (foot === 0 ? -1 : 1) * BONES_X_SHIFT;
    mesh.position.add(dist);
    mesh.updateMatrixWorld(true);
  }

  /** Re-snaps the heel mesh + drag handle to the current insole heel center. */
  realignToInsole(foot: number, insoleGeometry?: THREE.BufferGeometry): void {
    const mesh = this.meshes[foot];
    if (!mesh) return;

    this.alignToInsole(mesh, foot, insoleGeometry);
    this.syncDragSphere(foot);
    this.rebuildCurveGroups();
  }

  /**
   * Scales the heel base mesh in X/Z with the same factors as an insole length
   * change, re-applies height, then snaps back to the insole heel center.
   */
  scaleWithLength(
    foot: number,
    scale: number,
    insoleGeometry?: THREE.BufferGeometry
  ): void {
    const mesh = this.meshes[foot];
    const baseGeometry = this.baseGeometries[foot];
    const joint = this.heightJoints[foot];
    if (!mesh || !baseGeometry || !Number.isFinite(scale)) return;
    if (scale === 1) {
      this.realignToInsole(foot, insoleGeometry);
      return;
    }

    baseGeometry.computeBoundingBox();
    const center = baseGeometry.boundingBox?.getCenter(new THREE.Vector3()) ?? new THREE.Vector3();
    const positions = baseGeometry.getAttribute('position');
    for (let i = 0; i < positions.count; i++) {
      positions.setX(i, (positions.getX(i) - center.x) * scale + center.x);
      positions.setZ(i, (positions.getZ(i) - center.z) * scale + center.z);
    }
    positions.needsUpdate = true;
    baseGeometry.computeBoundingBox();
    baseGeometry.computeBoundingSphere();

    if (joint) {
      joint.center.x = (joint.center.x - center.x) * scale + center.x;
      joint.center.z = (joint.center.z - center.z) * scale + center.z;
    }

    const height = joint?.value;
    if (height != null) this.setHeight(height, foot);
    this.realignToInsole(foot, insoleGeometry);
  }

  private rebuildCurveGroups(): THREE.Group[] {
    this.disposeCurveGroups();

    const groups: THREE.Group[] = [];
    this.meshes.forEach((mesh, foot) => {
      const group = buildCurveGroup(mesh, foot);
      if (group) groups[foot] = group;
    });
    this.curveGroups = groups;

    const design = useDesignStore.getState().insoleDesign;
    design?.insoles.forEach((insole, foot) => {
      if (groups[foot]) insole.curvePoints = groups[foot];
    });

    return groups;
  }

  private disposeCurveGroups(): void {
    this.curveGroups.forEach(group => {
      group.traverse(object => {
        if (!(object instanceof THREE.Mesh) && !(object instanceof THREE.Line)) return;
        object.geometry.dispose();
        if (Array.isArray(object.material)) object.material.forEach(material => material.dispose());
        else object.material.dispose();
      });
    });
    this.curveGroups = [];
  }

  private disposeDragSpheres(): void {
    this.dragSpheres.forEach(sphere => {
      if (!sphere) return;
      sphere.removeFromParent();
      sphere.geometry.dispose();
      if (Array.isArray(sphere.material)) sphere.material.forEach(material => material.dispose());
      else sphere.material.dispose();
    });
    this.dragSpheres = [];
  }

}

const resolveDragVertexId = (vertexCount: number): number => {
  if (HighHeelDragIndex >= 0 && HighHeelDragIndex < vertexCount) {
    return HighHeelDragIndex;
  }
  console.warn(
    `HighHeelDragIndex (${HighHeelDragIndex}) is out of range for the heel mesh ` +
    `(${vertexCount} vertices). Falling back to HighHeelHeight.centerId.`
  );
  return HighHeelHeight.centerId;
};

const isFootVisible = (foot: number, feetView: HighHeelFeetView): boolean =>
  feetView === 'both' ||
  (feetView === 'right' && foot === 0) ||
  (feetView === 'left' && foot === 1);

const findVertexWeight = (joint: HighLevelJoint, vertexId: number): number => {
  for (const group of joint.vertices) {
    if (group.ids.includes(vertexId)) return group.weight || 1;
  }
  return 1;
};

const createDragSphere = (mesh: THREE.Mesh, vertexId: number, foot: number): THREE.Mesh => {
  mesh.updateMatrixWorld(true);
  const positions = mesh.geometry.getAttribute('position');
  const world = mesh.localToWorld(
    new THREE.Vector3().fromBufferAttribute(positions, vertexId)
  );

  const sphere = new THREE.Mesh(
    new THREE.SphereGeometry(DRAG_SPHERE_RADIUS, 16, 16),
    new THREE.MeshBasicMaterial({
      color: DRAG_SPHERE_COLOR,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
    })
  );
  sphere.name = `highHeelDrag_${foot === 0 ? 'right' : 'left'}`;
  sphere.position.copy(world);
  sphere.userData.jointType = HIGH_HEEL_JOINT_TYPE;
  sphere.userData.jointName = 'High Heel Height';
  sphere.userData.foot = foot;
  sphere.userData.vertexId = vertexId;
  sphere.userData.originalPosition = world.clone();
  sphere.renderOrder = 2;
  enlargeSphereRaycast(sphere, 2);
  return sphere;
};

/** Widens the raycast hit area so the handle is easier to grab on touch screens. */
const enlargeSphereRaycast = (mesh: THREE.Mesh, multiplier = 2.5): void => {
  const tmpSphere = new THREE.Sphere();
  const tmpCenter = new THREE.Vector3();
  const tmpHitPoint = new THREE.Vector3();

  mesh.geometry.computeBoundingSphere();

  mesh.raycast = function (raycaster, intersects) {
    const boundingSphere = mesh.geometry.boundingSphere;
    if (!boundingSphere) return;

    this.updateWorldMatrix(true, false);

    tmpCenter.copy(boundingSphere.center).applyMatrix4(this.matrixWorld);
    const worldScale = new THREE.Vector3();
    this.getWorldScale(worldScale);
    const maxScale = Math.max(worldScale.x, worldScale.y, worldScale.z);
    tmpSphere.center.copy(tmpCenter);
    tmpSphere.radius = boundingSphere.radius * maxScale * multiplier;

    if (!raycaster.ray.intersectSphere(tmpSphere, tmpHitPoint)) return;

    const distance = raycaster.ray.origin.distanceTo(tmpHitPoint);
    intersects.push({
      distance,
      point: tmpHitPoint.clone(),
      object: this,
    } as THREE.Intersection);
  };
};

/** Builds the sphere group describing the lift curve read off a heel mesh. */
const buildCurveGroup = (mesh: THREE.Mesh, foot: number): THREE.Group | null => {
  mesh.updateMatrixWorld(true);
  const positions = mesh.geometry.getAttribute('position');
  if (!positions) return null;

  const validVertexIds = HighHeelCurve.filter(
    vertexId => vertexId >= 0 && vertexId < positions.count
  );
  const points = validVertexIds
    .map(vertexId => mesh.localToWorld(new THREE.Vector3().fromBufferAttribute(positions, vertexId)))
    .sort((a, b) => a.z - b.z);
  if (points.length < 2) return null;

  const xConst = points[0].x;
  points.forEach(point => { point.x = xConst; });

  const insoleGeometry =
    useDesignStore.getState().insoleDesign?.insoles[foot]?.lowpolyBufferGeometry;
  insoleGeometry?.computeBoundingBox();

  const first = points[0].clone();
  const last = points[points.length - 1].clone();
  first.sub(last).multiplyScalar(0.5).add(points[0]);
  first.y = first.y - (first.y - points[0].y) * 0.5;
  points.unshift(first);
  last.z = (insoleGeometry?.boundingBox?.max.z ?? last.z) + 30;
  points.push(last);

  const group = new THREE.Group();
  group.name = `highHeelCurve_${foot === 0 ? 'right' : 'left'}`;

  points.forEach((point, pointIndex) => {
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(1.2, 8, 8),
      new THREE.MeshBasicMaterial({
        color: 0x0000ff,
        transparent: true,
        opacity: 0.8,
        depthTest: false,
      })
    );
    sphere.name = `highHeelCurvePoint_${pointIndex}`;
    sphere.position.copy(point);
    sphere.userData.pointIndex = pointIndex;
    sphere.userData.vertexId = pointIndex === 0 ? null : validVertexIds[pointIndex - 1];
    sphere.userData.foot = foot;
    sphere.renderOrder = 3;
    group.add(sphere);
  });

  return group;
};
